// ============================================================================
// notasFiscaisStorage — CRUD de notas fiscais (Supabase REST)
// ============================================================================

import { SUPABASE_URL, restHeaders } from './secureRest.js';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const asUuidOrNull = (v) => (typeof v === 'string' && UUID_RE.test(v.trim()) ? v.trim() : null);

function toApp(row) {
  if (!row) return null;
  return {
    id: row.id,
    tipo: row.tipo || 'nfse',
    numero: row.numero || '',
    serie: row.serie || '1',
    dataEmissao: row.data_emissao || '',
    natureza: row.natureza || '',
    destinatario: row.destinatario || {},
    destSource: row.dest_source || 'manual',
    destClientId: row.dest_client_id || '',
    destSupplierId: row.dest_supplier_id || '',
    items: row.items || [],
    valorTotal: Number(row.valor_total) || 0,
    formaPagamento: row.forma_pagamento || '',
    status: row.status || 'emitida',
    comandaId: row.comanda_id || '',
    unitId: row.unit_id || '',
    xmlRef: row.xml_ref || '',
    pdfUrl: row.pdf_url || '',
    observacoes: row.observacoes || '',
    createdAt: row.created_at,
    createdBy: row.created_by || '',
    updatedAt: row.updated_at,
  };
}

function toDb(nf) {
  const r = {};
  if (nf.tipo !== undefined) r.tipo = nf.tipo || 'nfse';
  if (nf.numero !== undefined) r.numero = nf.numero || null;
  if (nf.serie !== undefined) r.serie = nf.serie || '1';
  if (nf.dataEmissao !== undefined) r.data_emissao = nf.dataEmissao;
  if (nf.natureza !== undefined) r.natureza = nf.natureza || null;
  if (nf.destinatario !== undefined) r.destinatario = nf.destinatario || {};
  if (nf.destSource !== undefined) r.dest_source = nf.destSource || 'manual';
  if (nf.destClientId !== undefined) r.dest_client_id = asUuidOrNull(nf.destClientId);
  if (nf.destSupplierId !== undefined) r.dest_supplier_id = asUuidOrNull(nf.destSupplierId);
  if (nf.items !== undefined) r.items = nf.items || [];
  if (nf.valorTotal !== undefined) r.valor_total = Number(nf.valorTotal) || 0;
  if (nf.formaPagamento !== undefined) r.forma_pagamento = nf.formaPagamento || null;
  if (nf.status !== undefined) r.status = nf.status || 'emitida';
  if (nf.comandaId !== undefined) r.comanda_id = nf.comandaId || null;
  if (nf.unitId !== undefined) r.unit_id = nf.unitId || null;
  if (nf.xmlRef !== undefined) r.xml_ref = nf.xmlRef || null;
  if (nf.pdfUrl !== undefined) r.pdf_url = nf.pdfUrl || null;
  if (nf.observacoes !== undefined) r.observacoes = nf.observacoes || null;
  if (nf.createdBy !== undefined) r.created_by = nf.createdBy || null;
  return r;
}

export async function listNotasFiscais({ unitId, startDate, endDate, status, limit = 2000 } = {}) {
  const parts = ['select=*', 'order=data_emissao.desc,created_at.desc', `limit=${Math.min(Number(limit) || 2000, 5000)}`];
  if (unitId) parts.push(`unit_id=eq.${encodeURIComponent(unitId)}`);
  if (startDate) parts.push(`data_emissao=gte.${startDate}`);
  if (endDate) parts.push(`data_emissao=lte.${endDate}`);
  if (status) parts.push(`status=eq.${encodeURIComponent(status)}`);
  const r = await fetch(`${SUPABASE_URL}/rest/v1/notas_fiscais?${parts.join('&')}`, { headers: restHeaders() });
  // REGRA LINEAR (15/07/2026): falha NUNCA vira lista vazia silenciosa
  if (!r.ok) throw new Error(`listNotasFiscais: HTTP ${r.status} ${await r.text().then(t => t.slice(0, 120)).catch(() => '')}`);
  return (await r.json()).map(toApp);
}

export async function upsertNotaFiscal(nf) {
  const row = { ...toDb(nf), updated_at: new Date().toISOString() };
  const isUpdate = nf.id && nf.id.length === 36 && nf.id.includes('-');
  if (isUpdate) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/notas_fiscais?id=eq.${nf.id}`, {
      method: 'PATCH',
      headers: restHeaders({ Prefer: 'return=representation' }),
      body: JSON.stringify(row),
    });
    if (!r.ok) throw new Error(`Falha ao atualizar NF: ${await r.text()}`);
    return toApp((await r.json())[0]);
  }
  const r = await fetch(`${SUPABASE_URL}/rest/v1/notas_fiscais`, {
    method: 'POST',
    headers: restHeaders({ Prefer: 'return=representation' }),
    body: JSON.stringify(row),
  });
  if (!r.ok) throw new Error(`Falha ao criar NF: ${await r.text()}`);
  return toApp((await r.json())[0]);
}

export async function deleteNotaFiscal(id) {
  const r = await fetch(`${SUPABASE_URL}/rest/v1/notas_fiscais?id=eq.${id}`, {
    method: 'DELETE', headers: restHeaders(),
  });
  if (!r.ok) throw new Error(`Falha ao excluir NF: ${await r.text()}`);
  return true;
}
